import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { MechanicUpdateDocumentModel } from 'src/app/models/archive/mechanic-update-document.model';
import { MechanicModel } from 'src/app/models/archive/mechanic.model';
import { MechanicService } from 'src/app/services/archive/mechanic.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-update-document-mechanic',
  templateUrl: './update-document-mechanic.component.html',
  styleUrls: ['./update-document-mechanic.component.scss'],
  standalone: false
})
export class UpdateDocumentMechanicComponent implements OnInit, OnDestroy {

  changeDocument: boolean = false;
  formMechanicUpdate: FormGroup = new FormGroup({});
  mechanicModel: MechanicModel = new MechanicModel();
  subscription: Subscription;

  constructor(private mechanicService: MechanicService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef) {
    this.validateForm(new MechanicModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.mechanicModel = inputs.mechanicContract;
      if (this.mechanicModel) {
        this.validateForm(this.mechanicModel);
      } else {
        console.warn('mechanicModel undefined');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  close() {
    const data: ModalObjectType = {};
    if (this.changeDocument) {
      data.params = { reload: true, mechanicModel: this.mechanicModel };
      this.ref.close(data);
    } else {
      data.params = { reload: false };
      this.ref.close(data);
    }
  }

  async getDigitVerification() {
    const document = this.formMechanicUpdate.get("document")?.value;
    if (document == '') {
      this.formMechanicUpdate.controls["digitVerification"].patchValue(null);
      this.toastHandlerService.showToast('error', `El documento ingresado no es válido.`);
      return;
    }
    try {
      const response = await firstValueFrom(this.mechanicService.getDigitVerification(document));
      this.formMechanicUpdate.controls["digitVerification"].setValue(response);
    } catch (error) {
      this.formMechanicUpdate.controls["digitVerification"].patchValue(null);
      console.error('Error al modificar el mecánico.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formMechanicUpdate.valid) {
      const modelMechanic = this.formMechanicUpdate.getRawValue() as MechanicUpdateDocumentModel;
      try {
        this.mechanicModel = await firstValueFrom(this.mechanicService.updateMechanicDocument(modelMechanic));
        this.validateForm(this.mechanicModel);
        this.changeDocument = true;
        this.close();
        this.toastHandlerService.showToast('success', `El documento del mecánico fue modificado exitosamente.`);
      } catch (error) {
        console.error('Error al modificar el mecánico.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      transporterId: {
        required: 'El campo Mecánico es obligatorio.',
        min: 'Debe seleccionar un Mecánico válido.'
      },
      document: {
        required: 'El campo Documento es obligatorio.',
        maxlength: 'El Documento no puede tener más de 15 caracteres.'
      },
      digitVerification: {
        required: 'El campo Dígito de Verificación es obligatorio.',
        min: 'El Dígito de Verificación no puede ser menor que 0.',
        max: 'El Dígito de Verificación no puede ser mayor que 9.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMechanicUpdate.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(mechanic: MechanicModel) {
    this.formMechanicUpdate = this.formBuilder.group({
      mechanicId: [mechanic.mechanicId, [Validators.required, Validators.min(1)]],
      document: [mechanic.document, [Validators.required, Validators.maxLength(15)]],
      digitVerification: [mechanic.digitVerification, [Validators.required, Validators.min(0), Validators.max(9)]]
    });
    setTimeout(() => this.formMechanicUpdate.controls["digitVerification"].disable());
  }

  validateFormControls() {
    Object.keys(this.formMechanicUpdate.controls).forEach((key) => {
      const control = this.formMechanicUpdate.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
